import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { Toast } from '../../UIData/_actions/UIDataActions'
import { InvalidatePaginationData } from '../../UserData/_actions/UserDataActions'
import type { ImageUploadQueueItem } from './ImageUploadQueue'
import { formatImageFileSize } from './imageFileMetadata'
import { MAX_IMAGE_UPLOAD_FILE_SIZE } from './imageUploadConfig'
import { canPreviewImage } from './imagePreviewDimensions'
import { readImageUploadResponse } from './imageUploadResponse'

interface UploadResponse {
  ok: boolean
  error: string
  canRetry: boolean
  uploadedImageIds: number[]
}

interface Props {
  listId: string
  uploadUrl: string
}

const PREVIEW_MAX_SIZE = 128
const MAX_FALLBACK_PREVIEW_FILE_SIZE = 6 * 1024 * 1024
const RECENT_UPLOADED_IMAGE_HIGHLIGHT_MS = 60000
const MAX_UPLOAD_PROCESSING_MS = 6 * 60 * 1000

const getUploadFingerprint = (file: File) => `${file.name.trim().toLowerCase()}::${file.size}`

const getPreviewDrawRect = (width: number, height: number) => {
  const ratio = Math.max(PREVIEW_MAX_SIZE / width, PREVIEW_MAX_SIZE / height)
  const drawWidth = width * ratio
  const drawHeight = height * ratio

  return {
    height: drawHeight,
    width: drawWidth,
    x: (PREVIEW_MAX_SIZE - drawWidth) / 2,
    y: (PREVIEW_MAX_SIZE - drawHeight) / 2,
  }
}

const createPreviewBitmap = async (file: File) => {
  const widthResizedBitmap = await createImageBitmap(file, {
    resizeWidth: PREVIEW_MAX_SIZE,
    resizeQuality: 'medium',
  })

  if (widthResizedBitmap.height >= PREVIEW_MAX_SIZE) {
    return widthResizedBitmap
  }

  widthResizedBitmap.close()

  return await createImageBitmap(file, {
    resizeHeight: PREVIEW_MAX_SIZE,
    resizeQuality: 'medium',
  })
}

const createPreviewBlobUrl = async (
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number
) => {
  const canvas = document.createElement('canvas')
  const drawRect = getPreviewDrawRect(sourceWidth, sourceHeight)

  canvas.width = PREVIEW_MAX_SIZE
  canvas.height = PREVIEW_MAX_SIZE
  canvas.getContext('2d')?.drawImage(source, drawRect.x, drawRect.y, drawRect.width, drawRect.height)

  return await new Promise<string | undefined>((resolve) => {
    canvas.toBlob(blob => {
      resolve(blob ? URL.createObjectURL(blob) : undefined)
    }, 'image/png')
  })
}

const createImagePreviewBlobUrl = async (file: File) => {
  if (!canPreviewImage(await file.slice(0, 64 * 1024).arrayBuffer())) {
    return undefined
  }

  if ('createImageBitmap' in window) {
    try {
      const bitmap = await createPreviewBitmap(file)

      try {
        return await createPreviewBlobUrl(bitmap, bitmap.width, bitmap.height)
      } finally {
        bitmap.close()
      }
    } catch (e) {
      if (file.size > MAX_FALLBACK_PREVIEW_FILE_SIZE) {
        return undefined
      }
    }
  }

  if (file.size > MAX_FALLBACK_PREVIEW_FILE_SIZE) {
    return undefined
  }

  const sourceUrl = URL.createObjectURL(file)

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()

      image.onload = () => resolve(image)
      image.onerror = reject
      image.src = sourceUrl
    })

    return await createPreviewBlobUrl(image, image.naturalWidth, image.naturalHeight)
  } finally {
    URL.revokeObjectURL(sourceUrl)
  }
}

const isUploadErrorMessage = (message: string, pattern: string) => (
  message.toLowerCase().includes(pattern.toLowerCase())
)

export default function useImageUploadQueue({ listId, uploadUrl }: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const maxFileSizeLabel = useMemo(() => formatImageFileSize(MAX_IMAGE_UPLOAD_FILE_SIZE), [])

  const uploadId = useRef(0)
  const uploadsRef = useRef<ImageUploadQueueItem[]>([])
  const uploadRequests = useRef<Map<string, XMLHttpRequest>>(new Map())
  const uploadFingerprints = useRef<Set<string>>(new Set())
  const refreshImagesTimeout = useRef<number | undefined>(undefined)
  const recentUploadedImageTimeouts = useRef<Map<number, number>>(new Map())
  const mounted = useRef(true)

  const [uploads, setUploads] = useState<ImageUploadQueueItem[]>([])
  const [recentUploadedImageIds, setRecentUploadedImageIds] = useState<Set<number>>(() => new Set())

  useEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false

      const requests = Array.from(uploadRequests.current.values())
      uploadRequests.current.clear()
      requests.forEach(request => request.abort())
      uploadFingerprints.current.clear()

      if (refreshImagesTimeout.current !== undefined) {
        window.clearTimeout(refreshImagesTimeout.current)
      }

      recentUploadedImageTimeouts.current.forEach(timeout => window.clearTimeout(timeout))
      recentUploadedImageTimeouts.current.clear()

      uploadsRef.current.forEach(upload => {
        if (upload.previewUrl) {
          URL.revokeObjectURL(upload.previewUrl)
        }
      })
    }
  }, [])

  const setUploadItems = useCallback((updater: (uploads: ImageUploadQueueItem[]) => ImageUploadQueueItem[]) => {
    if (!mounted.current) {
      return
    }

    const nextUploads = updater(uploadsRef.current)

    uploadsRef.current = nextUploads
    setUploads(nextUploads)
  }, [])

  const updateUpload = useCallback((id: string, update: Partial<ImageUploadQueueItem>) => {
    setUploadItems(value => value.map(upload => (
      upload.id === id ? { ...upload, ...update } : upload
    )))
  }, [setUploadItems])

  const updateUploadPreview = useCallback((id: string, previewUrl: string) => {
    const existing = uploadsRef.current.find(upload => upload.id === id)
    if (!mounted.current || existing === undefined) {
      URL.revokeObjectURL(previewUrl)
      return
    }

    setUploadItems(value => value.map(upload => {
      if (upload.id !== id) {
        return upload
      }

      return {
        ...upload,
        previewUrl,
      }
    }))

    if (existing.previewUrl && existing.previewUrl !== previewUrl) {
      URL.revokeObjectURL(existing.previewUrl)
    }
  }, [setUploadItems])

  const forgetUploadFingerprint = useCallback((upload?: ImageUploadQueueItem) => {
    if (!upload?.fingerprint || upload.isDuplicate) {
      return
    }

    uploadFingerprints.current.delete(upload.fingerprint)
  }, [])

  const scheduleImagesRefresh = useCallback(() => {
    if (refreshImagesTimeout.current !== undefined) {
      window.clearTimeout(refreshImagesTimeout.current)
    }

    refreshImagesTimeout.current = window.setTimeout(() => {
      dispatch(InvalidatePaginationData('images', listId))
      refreshImagesTimeout.current = undefined
    }, 350)
  }, [dispatch, listId])

  const trackUploadedImageIds = useCallback((imageIds: number[]) => {
    const nextImageIds = Array.from(new Set(imageIds.filter(Number.isFinite)))

    if (nextImageIds.length === 0) {
      return
    }

    setRecentUploadedImageIds(value => {
      const nextValue = new Set(value)
      nextImageIds.forEach(imageId => nextValue.add(imageId))

      return nextValue
    })

    nextImageIds.forEach(imageId => {
      const existingTimeout = recentUploadedImageTimeouts.current.get(imageId)

      if (existingTimeout !== undefined) {
        window.clearTimeout(existingTimeout)
      }

      const timeout = window.setTimeout(() => {
        if (!mounted.current) {
          return
        }

        setRecentUploadedImageIds(value => {
          if (!value.has(imageId)) {
            return value
          }

          const nextValue = new Set(value)
          nextValue.delete(imageId)

          return nextValue
        })
        recentUploadedImageTimeouts.current.delete(imageId)
      }, RECENT_UPLOADED_IMAGE_HIGHLIGHT_MS)

      recentUploadedImageTimeouts.current.set(imageId, timeout)
    })
  }, [])

  const getFileTooLargeError = useCallback(() => (
    t('Images must be {{size}} or smaller', { size: maxFileSizeLabel })
  ), [maxFileSizeLabel, t])

  const getRejectedUploadError = useCallback((errors: FileRejection['errors']): string => {
    const errorCode = errors[0]?.code

    if (errorCode === 'file-invalid-type') {
      return t('Only JPG, PNG, WebP, TIFF, BMP, and GIF images can be uploaded')
    }

    if (errorCode === 'file-too-large') {
      return getFileTooLargeError()
    }

    if (errorCode === 'file-too-small') {
      return t('This file is too small to upload')
    }

    if (errorCode === 'too-many-files') {
      return t('Too many files selected')
    }

    return t('This file could not be added')
  }, [getFileTooLargeError, t])

  const getServerUploadError = useCallback((message: string | undefined, status: number): Pick<UploadResponse, 'error' | 'canRetry'> => {
    const fallbackError = t('This file could not be uploaded')

    if (status === 413) {
      return {
        error: getFileTooLargeError(),
        canRetry: false,
      }
    }

    if (!message) {
      return {
        error: fallbackError,
        canRetry: true,
      }
    }

    if (
      isUploadErrorMessage(message, 'upload_max_filesize')
      || isUploadErrorMessage(message, 'MAX_FILE_SIZE')
      || isUploadErrorMessage(message, 'maximum allowed size')
    ) {
      return {
        error: getFileTooLargeError(),
        canRetry: false,
      }
    }

    if (
      isUploadErrorMessage(message, 'Invalid file extension')
      || isUploadErrorMessage(message, 'Invalid image format')
      || isUploadErrorMessage(message, 'File upload stopped by extension')
    ) {
      return {
        error: t('Only JPG, PNG, WebP, TIFF, BMP, and GIF images can be uploaded'),
        canRetry: false,
      }
    }

    if (isUploadErrorMessage(message, 'File appears to be corrupted')) {
      return {
        error: t('This file appears to be corrupted'),
        canRetry: false,
      }
    }

    if (isUploadErrorMessage(message, 'Image dimensions are invalid')) {
      return {
        error: t('This image has invalid dimensions'),
        canRetry: false,
      }
    }

    if (isUploadErrorMessage(message, 'Animated or multipage images are not supported')) {
      return {
        error: t('Animated images and multipage files are not supported. Please upload a single still image.'),
        canRetry: false,
      }
    }

    if (isUploadErrorMessage(message, 'Image dimensions exceed upload limits')) {
      return {
        error: t('This image is too large to process. Please reduce its dimensions.'),
        canRetry: false,
      }
    }

    if (isUploadErrorMessage(message, 'Image processing failed')) {
      return {
        error: t('We could not process this image. Try saving it again and re-uploading it.'),
        canRetry: false,
      }
    }

    if (isUploadErrorMessage(message, 'partially uploaded')) {
      return {
        error: t('The upload was interrupted before it finished'),
        canRetry: true,
      }
    }

    if (
      isUploadErrorMessage(message, 'No file was uploaded')
      || isUploadErrorMessage(message, 'File has no name')
    ) {
      return {
        error: t('No file was uploaded'),
        canRetry: false,
      }
    }

    if (
      isUploadErrorMessage(message, 'Missing a temporary folder')
      || isUploadErrorMessage(message, 'Failed to write the uploaded file on disk')
    ) {
      return {
        error: t('The server could not save this file. Please try again.'),
        canRetry: true,
      }
    }

    return {
      error: fallbackError,
      canRetry: true,
    }
  }, [getFileTooLargeError, t])

  const parseUploadResponse = useCallback((request: XMLHttpRequest): UploadResponse => {
    const response = readImageUploadResponse(request.status, request.responseText)
    return {
      ...response,
      ...getServerUploadError(response.error, request.status),
    }
  }, [getServerUploadError])

  const startUpload = useCallback((upload: ImageUploadQueueItem) => {
    if (!mounted.current || !uploadsRef.current.some(item => item.id === upload.id) || uploadRequests.current.has(upload.id)) return

    const data = new FormData()
    data.append('file', upload.file)

    const request = new XMLHttpRequest()
    const startedAt = Date.now()
    uploadRequests.current.set(upload.id, request)

    request.upload.onprogress = (e) => {
      if (!e.lengthComputable || uploadRequests.current.get(upload.id) !== request) {
        return
      }

      const progress = Math.round((e.loaded / e.total) * 100)

      updateUpload(upload.id, {
        progress,
        progressKnown: true,
        status: progress >= 100 ? 'processing' : 'uploading',
      })
    }

    request.upload.onload = () => {
      if (uploadRequests.current.get(upload.id) !== request) {
        return
      }

      // XHR measures timeout from send(). Allow the transfer to take as long as
      // needed, then bound server processing beyond PHP's five-minute limit.
      request.timeout = Date.now() - startedAt + MAX_UPLOAD_PROCESSING_MS
      updateUpload(upload.id, {
        progress: 100,
        progressKnown: true,
        status: 'processing',
      })
    }

    request.onload = () => {
      if (uploadRequests.current.get(upload.id) !== request) return
      uploadRequests.current.delete(upload.id)

      const uploadResponse = parseUploadResponse(request)

      if (uploadResponse.ok) {
        updateUpload(upload.id, {
          progress: 100,
          progressKnown: true,
          status: 'done',
          error: undefined,
          canRetry: true,
        })
        trackUploadedImageIds(uploadResponse.uploadedImageIds)
        scheduleImagesRefresh()
        return
      }

      updateUpload(upload.id, {
        status: 'error',
        error: uploadResponse.error,
        canRetry: uploadResponse.canRetry,
      })
      dispatch(Toast(uploadResponse.error))
    }

    request.onerror = () => {
      if (uploadRequests.current.get(upload.id) !== request) return
      uploadRequests.current.delete(upload.id)
      const networkError = t('Network error. Check your connection and try again.')

      updateUpload(upload.id, {
        status: 'error',
        error: networkError,
        canRetry: true,
      })
      dispatch(Toast(networkError))
    }

    request.ontimeout = () => {
      if (uploadRequests.current.get(upload.id) !== request) return
      uploadRequests.current.delete(upload.id)
      const timeoutError = t('The server took too long to process this image. Please try again.')

      updateUpload(upload.id, {
        status: 'error',
        error: timeoutError,
        canRetry: true,
      })
      dispatch(Toast(timeoutError))
    }

    request.onabort = () => {
      if (uploadRequests.current.get(upload.id) !== request) return
      uploadRequests.current.delete(upload.id)
      updateUpload(upload.id, {
        status: 'canceled',
        error: undefined,
        canRetry: true,
      })
    }

    updateUpload(upload.id, {
      progress: 0,
      progressKnown: false,
      status: 'uploading',
      error: undefined,
      canRetry: true,
    })

    request.open('POST', uploadUrl)
    request.withCredentials = true
    request.send(data)
  }, [dispatch, parseUploadResponse, scheduleImagesRefresh, t, trackUploadedImageIds, updateUpload, uploadUrl])

  const createUpload = useCallback((file: File): ImageUploadQueueItem => ({
    id: `image-upload-${Date.now()}-${uploadId.current++}`,
    file,
    name: file.name,
    size: file.size,
    fingerprint: getUploadFingerprint(file),
    progress: 0,
    progressKnown: false,
    status: 'uploading',
    canRetry: true,
  }), [])

  const createDuplicateUpload = useCallback((file: File): ImageUploadQueueItem => ({
    id: `image-upload-${Date.now()}-${uploadId.current++}`,
    file,
    name: file.name,
    size: file.size,
    fingerprint: getUploadFingerprint(file),
    isDuplicate: true,
    progress: 0,
    progressKnown: true,
    status: 'error',
    error: t('This file is already in the upload queue'),
    canRetry: false,
  }), [t])

  const createRejectedUpload = useCallback(({ file, errors }: FileRejection): ImageUploadQueueItem => ({
    id: `image-upload-${Date.now()}-${uploadId.current++}`,
    file,
    name: file.name,
    size: file.size,
    progress: 0,
    progressKnown: true,
    status: 'error',
    error: getRejectedUploadError(errors),
    canRetry: false,
  }), [getRejectedUploadError])

  const createUploadPreview = useCallback((upload: ImageUploadQueueItem) => {
    createImagePreviewBlobUrl(upload.file)
      .then(previewUrl => {
        if (previewUrl) {
          updateUploadPreview(upload.id, previewUrl)
        }
      })
      .catch(() => undefined)
  }, [updateUploadPreview])

  const onUploadAccepted = useCallback((files: File[]) => {
    if (files.length === 0) {
      return
    }

    const uploadsToStart: ImageUploadQueueItem[] = []
    const nextUploads = files.map(file => {
      const fingerprint = getUploadFingerprint(file)

      if (uploadFingerprints.current.has(fingerprint)) {
        return createDuplicateUpload(file)
      }

      uploadFingerprints.current.add(fingerprint)
      const upload = createUpload(file)
      uploadsToStart.push(upload)

      return upload
    })

    setUploadItems(value => [...nextUploads, ...value])

    if (uploadsToStart.length === 0) {
      return
    }

    window.setTimeout(() => {
      if (!mounted.current) {
        return
      }

      uploadsToStart.forEach(createUploadPreview)
    }, 0)
    window.requestAnimationFrame(() => {
      if (!mounted.current) {
        return
      }

      uploadsToStart.forEach(startUpload)
    })
  }, [createDuplicateUpload, createUpload, createUploadPreview, setUploadItems, startUpload])

  const onUploadRejected = useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections.length === 0) {
      return
    }

    const nextUploads = fileRejections.map(createRejectedUpload)

    setUploadItems(value => [...nextUploads, ...value])
  }, [createRejectedUpload, setUploadItems])

  const onCancelUpload = useCallback((id: string) => {
    uploadRequests.current.get(id)?.abort()
  }, [])

  const onRetryUpload = useCallback((id: string) => {
    const upload = uploadsRef.current.find(upload => upload.id === id)

    if (!upload || upload.canRetry === false || !['error', 'canceled', 'done'].includes(upload.status)) {
      return
    }

    startUpload({
      ...upload,
      progress: 0,
      progressKnown: false,
      status: 'uploading',
      error: undefined,
      canRetry: true,
    })
  }, [startUpload])

  const onRemoveUpload = useCallback((id: string) => {
    const upload = uploadsRef.current.find(upload => upload.id === id)

    uploadRequests.current.get(id)?.abort()
    uploadRequests.current.delete(id)

    if (upload?.previewUrl) {
      URL.revokeObjectURL(upload.previewUrl)
    }

    forgetUploadFingerprint(upload)
    setUploadItems(value => value.filter(upload => upload.id !== id))
  }, [forgetUploadFingerprint, setUploadItems])

  const onClearCompletedUploads = useCallback(() => {
    uploadsRef.current
      .filter(upload => upload.status === 'done')
      .forEach(upload => {
        if (upload.previewUrl) {
          URL.revokeObjectURL(upload.previewUrl)
        }

        forgetUploadFingerprint(upload)
      })

    setUploadItems(value => value.filter(upload => upload.status !== 'done'))
  }, [forgetUploadFingerprint, setUploadItems])

  const onClearFailedUploads = useCallback(() => {
    uploadsRef.current
      .filter(upload => upload.status === 'error' || upload.status === 'canceled')
      .forEach(upload => {
        if (upload.previewUrl) {
          URL.revokeObjectURL(upload.previewUrl)
        }

        forgetUploadFingerprint(upload)
      })

    setUploadItems(value => value.filter(upload => upload.status !== 'error' && upload.status !== 'canceled'))
  }, [forgetUploadFingerprint, setUploadItems])

  return useMemo(() => ({
    uploads,
    recentUploadedImageIds,
    onUploadAccepted,
    onUploadRejected,
    onCancelUpload,
    onRetryUpload,
    onRemoveUpload,
    onClearCompletedUploads,
    onClearFailedUploads,
  }), [
    onCancelUpload,
    onClearCompletedUploads,
    onClearFailedUploads,
    onRemoveUpload,
    onRetryUpload,
    onUploadAccepted,
    onUploadRejected,
    recentUploadedImageIds,
    uploads,
  ])
}
